import React, { ComponentType, Key, ReactNode } from "react";
import { Control, Controller, DeepMap, FieldError } from "react-hook-form";
import ReactSelect, {
  ActionMeta,
  components as Components,
  createFilter,
  CSSObjectWithLabel,
  DropdownIndicatorProps,
  GroupBase,
  GroupHeadingProps,
  MenuListProps,
  MultiValue,
  MultiValueGenericProps,
  MultiValueRemoveProps,
  OptionProps,
  OptionsOrGroups,
  Props,
  SingleValue,
} from "react-select";
import AsyncSelect from "react-select/async";
import Creatable from "react-select/creatable";
import { WindowedMenuList } from "react-windowed-select";
import { Label, Loader } from "semantic-ui-react";
import { errorMessageSelector } from "../../../../features/data-filters/components/Utils";
import { Readonly } from "../../../ControlledForm";
import { useId } from "react-aria";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/pro-solid-svg-icons";
import { SingleValueProps } from "react-select";
import { ControlProps } from "react-select";
import { ValueContainerProps } from "react-select";
import { InputProps } from "react-select";
import { MenuProps } from "react-select";
import { IndicatorsContainerProps } from "react-select";

export type AccessibleDropdownOption<T = any> = {
  key?: string | number;
  value: T;
  label: string;
  color?: string;
  isDisabled?: boolean;
  isFixed?: boolean;
};

interface IControlledAccessibleDropdownStyles {
  control?: CSSObjectWithLabel;
  menuPortal?: CSSObjectWithLabel;
}

interface IControlledAccessibleDropdownClassNames {
  control?: string;
  multiValue?: string;
}

/**
 * The components of the dropdown can be swapped out for custom components. For a full list, see the link
 * @see https://react-select.com/components#replaceable-components
 *
 * For the default values of the components, see the MultiValueLabel example. The defaults for the other
 * components should be in the same place.
 */
export type CustomMultiValueLabel = ComponentType<
  MultiValueGenericProps<AccessibleDropdownOption, boolean, GroupBase<AccessibleDropdownOption>>
>;
export type CustomMultiValueContainer = ComponentType<
  MultiValueGenericProps<AccessibleDropdownOption, boolean, GroupBase<AccessibleDropdownOption>>
>;
export type CustomMultiValueRemove = ComponentType<
  MultiValueRemoveProps<AccessibleDropdownOption, boolean, GroupBase<AccessibleDropdownOption>>
>;
export type CustomSingleValue = ComponentType<
  SingleValueProps<AccessibleDropdownOption, boolean, GroupBase<AccessibleDropdownOption>>
>;
export type CustomValueContainer = ComponentType<
  ValueContainerProps<AccessibleDropdownOption, boolean, GroupBase<AccessibleDropdownOption>>
>;
export type CustomIndicatorsContainer = ComponentType<
  IndicatorsContainerProps<AccessibleDropdownOption, boolean, GroupBase<AccessibleDropdownOption>>
>;
export type CustomInput = ComponentType<
  InputProps<AccessibleDropdownOption, boolean, GroupBase<AccessibleDropdownOption>>
>;
export type CustomGroupHeading = ComponentType<
  GroupHeadingProps<AccessibleDropdownOption, boolean, GroupBase<AccessibleDropdownOption>>
>;
export type CustomOption = ComponentType<
  OptionProps<AccessibleDropdownOption, boolean, GroupBase<AccessibleDropdownOption>>
>;
export type CustomMenuList = ComponentType<
  MenuListProps<AccessibleDropdownOption, boolean, GroupBase<AccessibleDropdownOption>>
>;
export type CustomMenu = ComponentType<
  MenuProps<AccessibleDropdownOption, boolean, GroupBase<AccessibleDropdownOption>>
>;
export type CustomDropdownIndicator = ComponentType<
  DropdownIndicatorProps<AccessibleDropdownOption, boolean, GroupBase<AccessibleDropdownOption>>
>;
export type CustomControl = ComponentType<
  ControlProps<AccessibleDropdownOption, boolean, GroupBase<AccessibleDropdownOption>>
>;
interface IControlledAccessibleDropdownComponents {
  multiValueLabel?: CustomMultiValueLabel;
  multiValueContainer?: CustomMultiValueContainer;
  multiValueRemove?: CustomMultiValueRemove;
  singleValue?: CustomSingleValue;
  valueContainer?: CustomValueContainer;
  indicatorsContainer?: CustomIndicatorsContainer;
  groupHeading?: CustomGroupHeading;
  option?: CustomOption;
  menuList?: CustomMenuList;
  menu?: CustomMenu;
  dropdownIndicator?: CustomDropdownIndicator;
  control?: CustomControl;
  input?: CustomInput;
}
export type CustomMenuFooterSelectProps = Props<
  AccessibleDropdownOption<any>,
  boolean,
  GroupBase<AccessibleDropdownOption<any>>
>;

interface AccessibleDropdown {
  placeholder?: string;
  clearable?: boolean;
  options?: OptionsOrGroups<AccessibleDropdownOption, GroupBase<AccessibleDropdownOption>>;
  multiple?: boolean;
  defaultValue?: AccessibleDropdownOption["value"] | null | any;
  searchByGroup?: boolean;
  useVirtualList?: boolean;
  rules?: any;
  readonly?: boolean;
  transform?: {
    input: (value: any) => AccessibleDropdownOption | AccessibleDropdownOption[] | null;
    output: (value: AccessibleDropdownOption | readonly AccessibleDropdownOption[] | null) => any;
  };
  components?: IControlledAccessibleDropdownComponents;
  styles?: IControlledAccessibleDropdownStyles;
  componentClassNames?: IControlledAccessibleDropdownClassNames;
  maxMenuHeight?: number;
  menuPlacement?: "auto" | "bottom" | "top";
  hideBorder?: boolean;
  allowAdditions?: boolean;
  onCreateOption?: (inputValue: string) => void;
  disabled?: boolean;
  className?: string;
  classNamePrefix?: string;
  label?: string;
  subLabel?: string;
  isLoading?: boolean;
  dropdownOnly?: boolean;
  required?: boolean;
  onInputChange?: (inputValue: string, actionMeta: { action: string }) => void;
  onSelect?: (selectedOption: AccessibleDropdownOption | null) => void;
  onClear?: () => void;
  menuFooter?: ReactNode;
  noOptionsMessage?: string | ((obj: { inputValue: string }) => ReactNode);
  menuPortalTarget?: HTMLElement;
  menuShouldScrollIntoView?: boolean;
  onMenuOpen?: () => void;
  onMenuClose?: () => void;
  closeMenuOnSelect?: boolean;
  // Async props
  isAsync?: boolean;
  loadOptions?: (inputValue: string, callback: (options: AccessibleDropdownOption[]) => void) => void;
  defaultOptions?: AccessibleDropdownOption[] | boolean;
  cacheOptions?: boolean;
  selectAllOption?: boolean; // Adds an option to select all options- only works for multiple select dropdowns (not yet implemented for async dropdowns)
  selectAllLabel?: string; // Label for the select all option
}

interface IAccessibleDropdown extends AccessibleDropdown {
  name?: string;
  onChange?: (newValue: MultiValue<AccessibleDropdownOption> | SingleValue<AccessibleDropdownOption>) => void;
  value: AccessibleDropdownOption["value"] | AccessibleDropdownOption["value"][] | null;
  id?: string;
  error?: string;
  showErrorLabel?: boolean;
  virtualDropdownProps?: AccessibleDropdown;
}

export function AccessibleDropdown({
  name,
  value,
  options,
  placeholder = "",
  onClear,
  clearable = false,
  multiple = false,
  defaultValue,
  searchByGroup = false,
  useVirtualList = true,
  transform = { input: (value) => value, output: (value) => value },
  onChange = () => {},
  onSelect,
  components,
  styles,
  componentClassNames,
  maxMenuHeight,
  menuPlacement,
  hideBorder = false,
  allowAdditions = false,
  onCreateOption,
  disabled = false,
  closeMenuOnSelect,
  className = "",
  classNamePrefix = "",
  id,
  error,
  showErrorLabel = true,
  label,
  subLabel,
  readonly = false,
  isLoading = false,
  dropdownOnly = false,
  required = false,
  onInputChange,
  menuFooter,
  noOptionsMessage,
  menuPortalTarget,
  menuShouldScrollIntoView,
  onMenuOpen,
  onMenuClose,
  selectAllOption = false,
  selectAllLabel = "Select All",
  // Async props
  isAsync = false,
  loadOptions,
  defaultOptions,
  cacheOptions = false,
}: IAccessibleDropdown) {
  const finalId = useId(id);

  const Component = isAsync ? AsyncSelect : allowAdditions ? Creatable : ReactSelect;

  const filterByGroupsAndOptions = ({ label, value }: { label: string; value: string }, query: string) => {
    const labelLowerCase = label.toLowerCase();
    const valueLowerCase = value.toLowerCase();
    const queryLowerCase = query.toLowerCase();

    if (labelLowerCase.includes(queryLowerCase) || valueLowerCase.includes(queryLowerCase)) return true;

    const matchingOptionsAndGroups =
      options?.filter((optionOrGroup) =>
        (optionOrGroup as GroupBase<AccessibleDropdownOption>).label?.toLowerCase().includes(queryLowerCase),
      ) || [];

    if (!!matchingOptionsAndGroups.length) {
      for (const optionOrGroup of matchingOptionsAndGroups) {
        const group = optionOrGroup as GroupBase<AccessibleDropdownOption>;
        const isGroup = !!group.options;
        if (isGroup) {
          const option = group.options.find(
            (option) => option.value.toLowerCase() === valueLowerCase || option.label.toLowerCase() === labelLowerCase,
          );
          if (!!option) return true;
        }
      }
    }
    return false;
  };

  const MenuListWithFooter = (
    props: MenuListProps<AccessibleDropdownOption, boolean, GroupBase<AccessibleDropdownOption>>,
  ) => {
    const MenuListComponent = useVirtualList ? WindowedMenuList : Components.MenuList;
    const CustomMenuList = components?.menuList || MenuListComponent;

    return (
      <div>
        <CustomMenuList {...props} />
        {menuFooter && (
          <div className='tw-border-x-0 tw-border-b-0 tw-border-t tw-border-solid tw-border-border'>
            {React.cloneElement(menuFooter as React.ReactElement, {
              selectProps: props.selectProps,
            })}
          </div>
        )}
      </div>
    );
  };

  // Common props for all select components
  const commonProps = {
    inputId: finalId,
    className: className,
    classNamePrefix: classNamePrefix,
    createOptionPosition: "first" as const,
    formatCreateLabel: (inputValue: string) => `Add "${inputValue}"`,
    isDisabled: disabled || isLoading,
    onCreateOption: (inputValue: string) => {
      onCreateOption && onCreateOption(inputValue);
    },
    onInputChange,
    openMenuOnFocus: true,
    maxMenuHeight,
    menuPlacement,
    menuPortalTarget,
    unstyled: true,
    closeMenuOnSelect,
    menuShouldScrollIntoView,
    onMenuOpen,
    onMenuClose,
    noOptionsMessage: ({ inputValue }: { inputValue: string }) => {
      if (typeof noOptionsMessage === "function") {
        return noOptionsMessage({ inputValue });
      }
      return noOptionsMessage || "No options";
    },
    components: {
      MultiValueLabel:
        components?.multiValueLabel !== undefined ? components.multiValueLabel : Components.MultiValueLabel,
      MultiValueContainer:
        components?.multiValueContainer !== undefined ? components.multiValueContainer : Components.MultiValueContainer,
      MultiValueRemove:
        components?.multiValueRemove !== undefined ? components.multiValueRemove : Components.MultiValueRemove,
      SingleValue: components?.singleValue !== undefined ? components.singleValue : Components.SingleValue,
      Control: components?.control !== undefined ? components.control : Components.Control,
      ValueContainer: components?.valueContainer !== undefined ? components.valueContainer : Components.ValueContainer,
      MenuList: menuFooter
        ? MenuListWithFooter
        : useVirtualList
          ? WindowedMenuList
          : components?.menuList || Components.MenuList,
      Menu: components?.menu !== undefined ? components.menu : Components.Menu,
      GroupHeading: components?.groupHeading !== undefined ? components.groupHeading : Components.GroupHeading,
      Option: components?.option !== undefined ? components.option : Components.Option,
      Input: components?.input !== undefined ? components.input : Components.Input,
      IndicatorsContainer:
        components?.indicatorsContainer !== undefined
          ? components.indicatorsContainer
          : (props: any) => <div className='tw-ml-3 tw-flex tw-items-center tw-gap-3'>{props.children}</div>,
      DropdownIndicator: (props: any) => {
        if (props.selectProps.isLoading) return null;
        if (props.hasValue && clearable) return null; // don't show dropdown arrow if already showing clear icon
        const IndicatorComponent = components?.dropdownIndicator;
        return IndicatorComponent ? (
          <IndicatorComponent {...props} />
        ) : (
          <i className='dropdown icon tw-m-0 tw-flex tw-h-3 tw-w-3 tw-items-center' />
        );
      },
      LoadingIndicator: () => <Loader active inline size='tiny' />,
      LoadingMessage: () => (
        <div className='tw-flex tw-w-full tw-items-center tw-justify-center tw-py-2'>
          <Loader active inline size='small' />
        </div>
      ),
      IndicatorSeparator: () => null,
      ClearIndicator: (props: any) => (
        <button
          className='tw-cursor-pointer tw-border-none tw-bg-transparent tw-p-0'
          aria-label='Clear'
          onClick={props.clearValue}
          onTouchEnd={props.clearValue}
        >
          <FontAwesomeIcon icon={faXmark} className='tw-h-5 tw-w-5 tw-cursor-pointer tw-text-black' />
        </button>
      ),
    },
    classNames: {
      multiValue: () =>
        componentClassNames?.multiValue ??
        "ui label basic tw-m-0 tw-py-1 tw-h-[20px] tw-px-2 tw-space-x-1 tw-bg-[#E8E8E8] tw-border-border tw-text-[#00000099]",
      control: () => componentClassNames?.control ?? "accessible-input",
    },
    styles: {
      control: (base: any, props: any) => ({
        ...base,
        "paddingRight": "10px",
        "display": "flex",
        "color": "rgba(0, 0, 0, 0.87)",
        "border": hideBorder ? 0 : "1px solid rgba(34, 36, 38, 0.15)",
        "backgroundColor": !!error ? "#FFF6F6" : "#FFFFFF",
        "borderRadius": props.menuIsOpen ? "0.28571429rem 0.28571429rem 0 0" : "0.28571429rem",
        "borderColor": !!error ? "#E0B4B4" : props.menuIsOpen ? "rgba(34, 36, 38, 0.15)" : undefined,
        "boxShadow": props.menuIsOpen ? "0px 2px 3px 0px rgba(34, 36, 38, 0.15)" : undefined,
        "borderBottomColor": props.menuIsOpen ? "rgba(0, 0, 0, 0)" : undefined,
        "transition": "none",
        ":hover": {
          cursor: "text",
        },
        ...((styles && styles?.control) || {}),
      }),
      valueContainer: (base: any, props: any) => ({
        ...base,
        color: "rgba(0, 1, 0, 0.87)",
        overflow: "auto",
        padding: "8px 0 8px 0",
        paddingLeft: props.isMulti && props.hasValue ? "7px" : "16px",
        gap: "3px",
      }),
      indicatorsContainer: (base: any) => ({
        ...base,
        "gap": "2px",
        "color": "rgba(0, 1, 0, 0.87)",
        ":hover": {
          cursor: "pointer",
        },
      }),
      menu: (base: any) => ({
        ...base,
        backgroundColor: "white",
        borderLeft: hideBorder ? 0 : "1px solid rgba(34, 36, 38, 0.15)",
        borderRight: hideBorder ? 0 : "1px solid rgba(34, 36, 38, 0.15)",
        borderBottom: hideBorder ? 0 : "1px solid rgba(34, 36, 38, 0.15)",
        borderColor: "#96C8DA",
        borderRadius: "0 0 0.28571429rem 0.28571429rem",
        boxShadow: "0px 2px 3px 0px rgba(34, 36, 38, 0.15)",
        marginTop: "-1px",
        overflow: "auto",
        zIndex: 3,
      }),
      menuPortal: (base: any) => ({
        ...base,
        ...((styles && styles?.menuPortal) || {}),
      }),
      option: (base: any, props: any) => {
        return {
          ...base,
          "padding": "8px 16px",
          "transition": "0.3s",
          "backgroundColor": (props.data as AccessibleDropdownOption)?.color
            ? (props.data as AccessibleDropdownOption)?.color
            : props.isSelected
              ? "#dcdfec"
              : props.isFocused
                ? "rgba(0, 0, 0, 0.03)"
                : undefined,
          "borderTop": "1px solid #FAFAFA",
          "color": props.isDisabled ? "rgba(0, 0, 0, 0.4)" : undefined,
          ":hover": {
            cursor: "pointer",
            backgroundColor: !(props.data as AccessibleDropdownOption)?.color ? "#dcdfec" : undefined,
            filter: (props.data as AccessibleDropdownOption)?.color ? "brightness(80%)" : undefined,
          },
        };
      },
      groupHeading: (base: any) => ({
        ...base,
        textTransform: "uppercase",
        color: "rgba(0, 0, 0, 0.85)",
        fontSize: "0.78571429em",
        fontWeight: "bold",
        padding: "16px 0px 4px 16px",
      }),
      multiValue: (base: any) => ({
        ...base,
        display: "flex !important",
        alignItems: "center",
        cursor: "default",
      }),
      multiValueRemove: (base: any, state: any) => ({
        ...base,
        cursor: "pointer",
        ...(state.data.isFixed ? { display: "none" } : {}),
      }),
      placeholder: (base: any) => ({
        ...base,
        color: "rgba(191, 191, 191, 0.87)",
      }),
      noOptionsMessage: (base: any) => ({
        ...base,
        padding: "8px 16px",
        borderTop: "1px solid #FAFAFA",
      }),
      clearIndicator: (base: any, state: any) => {
        const valuesArray =
          (state.isMulti
            ? (state.selectProps.value as MultiValue<AccessibleDropdownOption>)
            : ([state.selectProps.value] as MultiValue<AccessibleDropdownOption>)) ?? [];
        const allValuesAreFixed = valuesArray.every((option: AccessibleDropdownOption) => option.isFixed);
        return {
          ...base,
          ...(allValuesAreFixed ? { display: "none" } : {}),
        };
      },
    },
    isClearable: clearable,
    isSearchable: !dropdownOnly,
    value: transform.input(value),
    onChange: (
      newValue: MultiValue<AccessibleDropdownOption<any>> | SingleValue<AccessibleDropdownOption<any>> | null,
      actionMeta: ActionMeta<AccessibleDropdownOption<any>>,
    ) => {
      const actionIsRemove = actionMeta.action === "remove-value" || actionMeta.action === "pop-value";
      const actionIsSelectAll = actionMeta.action === "select-option" && actionMeta?.option?.value === "select_all";
      const shouldSuppressAction = actionIsRemove && actionMeta.removedValue.isFixed;
      if (shouldSuppressAction) return;

      const actionIsClear = actionMeta.action === "clear";
      if (actionIsClear) onClear?.();
      if (actionIsClear && multiple) {
        // Remove all values except fixed ones
        const removedFixedValues = actionMeta.removedValues.filter((value) => value.isFixed);
        const newValueWithFixedValuesReplaced = [...removedFixedValues, ...(newValue as AccessibleDropdownOption[])];
        onChange(transform.output(newValueWithFixedValuesReplaced));
        return;
      }

      if (actionIsSelectAll) {
        onChange((options ?? []).map((option) => transform.output(option as AccessibleDropdownOption)));
        onSelect?.(null);
        return;
      }

      if (newValue !== null) {
        onChange(transform.output(newValue));
        onSelect?.(Array.isArray(newValue) ? null : (newValue as AccessibleDropdownOption));
        return;
      }
      onChange(transform.output(multiple ? [] : null));
      onSelect?.(null);
    },
    name,
    isMulti: multiple,
    defaultValue: transform.input(defaultValue),
    placeholder,
    isLoading,
    required,
  };

  // Show "Select All" option only if multiple select is enabled, the prop is enabled, and not all options are already selected
  const showSelectAllOption = multiple && selectAllOption && value.length < (options ?? []).length;

  // Async-specific props
  const asyncProps = isAsync
    ? {
        loadOptions,
        defaultOptions,
        cacheOptions,
      }
    : {
        options: showSelectAllOption
          ? [...(options as AccessibleDropdownOption[]), { label: selectAllLabel, value: "select_all" }]
          : options,
        filterOption: !!searchByGroup
          ? filterByGroupsAndOptions
          : createFilter({ ignoreAccents: false, stringify: (option) => option.label }),
      };

  return (
    <>
      {!!label && <label htmlFor={finalId}>{label}</label>}
      {subLabel && <div className='-tw-mt-1 tw-mb-1 tw-text-sm tw-text-grey'>{subLabel}</div>}
      {readonly && !multiple && (
        <Readonly as='span' value={(transform.input(value ?? defaultValue) as { label: string })?.label} />
      )}
      {readonly && multiple && (
        <Readonly
          className={className}
          as='span'
          value={(transform.input(value ?? defaultValue) as AccessibleDropdownOption[] | null)
            ?.map((option) => {
              return option.label;
            })
            .join(" • ")}
        />
      )}
      {!readonly && <Component {...commonProps} {...asyncProps} />}
      {error && showErrorLabel && (
        <Label basic prompt pointing='above'>
          {error}
        </Label>
      )}
    </>
  );
}

interface IControlledAccessibleDropdown extends AccessibleDropdown {
  control: Control;
  name: string;
  errors?: DeepMap<Record<Key, unknown>, FieldError>;
}

export function ControlledAccessibleDropdown({
  name,
  control,
  options,
  label,
  subLabel,
  placeholder = "",
  errors,
  clearable = false,
  multiple = false,
  defaultValue,
  searchByGroup = false,
  useVirtualList = true,
  rules,
  readonly = false,
  transform = { input: (value) => value, output: (value) => value },
  onCreateOption = () => {},
  onSelect,
  components,
  styles,
  closeMenuOnSelect,
  maxMenuHeight,
  menuPlacement,
  hideBorder = false,
  allowAdditions = false,
  disabled = false,
  className = "",
  classNamePrefix = "",
  isLoading = false,
  required = false,
  onInputChange,
  dropdownOnly = false,
  onClear,
  menuFooter,
  noOptionsMessage,
  menuPortalTarget,
  menuShouldScrollIntoView,
  selectAllOption = false,
  selectAllLabel = "Select All",
  // Async props
  isAsync = false,
  loadOptions,
  defaultOptions,
  cacheOptions = false,
}: IControlledAccessibleDropdown) {
  return (
    <Controller
      name={name}
      control={control}
      defaultValue={defaultValue}
      rules={rules}
      render={({ onChange, value, name }) => {
        return (
          <AccessibleDropdown
            name={name}
            options={options}
            placeholder={placeholder}
            onChange={(value) => onChange(value)}
            onClear={onClear}
            components={components}
            styles={styles}
            maxMenuHeight={maxMenuHeight}
            menuPlacement={menuPlacement}
            hideBorder={hideBorder}
            allowAdditions={allowAdditions}
            closeMenuOnSelect={closeMenuOnSelect}
            onCreateOption={onCreateOption}
            disabled={disabled}
            className={className}
            classNamePrefix={classNamePrefix}
            value={value}
            clearable={clearable}
            multiple={multiple}
            defaultValue={defaultValue}
            searchByGroup={searchByGroup}
            useVirtualList={useVirtualList}
            onSelect={onSelect}
            transform={transform}
            id={name}
            error={errorMessageSelector(errors)(name)}
            label={label}
            subLabel={subLabel}
            readonly={readonly}
            isLoading={isLoading}
            required={required}
            onInputChange={onInputChange}
            dropdownOnly={dropdownOnly}
            menuFooter={menuFooter}
            noOptionsMessage={noOptionsMessage}
            menuPortalTarget={menuPortalTarget}
            menuShouldScrollIntoView={menuShouldScrollIntoView}
            selectAllOption={selectAllOption}
            selectAllLabel={selectAllLabel}
            // Async props
            isAsync={isAsync}
            loadOptions={loadOptions}
            defaultOptions={defaultOptions}
            cacheOptions={cacheOptions}
          />
        );
      }}
    />
  );
}

/**
 * Transform for single select picklists with custom labels
 * @param labelMap A dictionary mapping values to labels
 * @param extra Extra properties to add to each option (useful for component overrides)
 * @returns
 */
export const picklistTransform = ({
  extra,
  labelMap,
}: { extra?: Record<string, any>; labelMap?: Record<string, string> } = {}) => ({
  input: (value: string): AccessibleDropdownOption => ({
    value,
    label: labelMap?.[value] || value,
    key: value,
    ...(extra || {}),
  }),
  output: (option: string | AccessibleDropdownOption | readonly AccessibleDropdownOption[] | null): string | null =>
    typeof option === "object" && option !== null ? (option as any).value : option,
});

/**
 * Transform for multiselect picklists with custom labels
 * @param labelMap A dictionary mapping values to labels
 * @param extra Extra properties to add to each option (useful for component overrides)
 * @returns
 */
export const multiPicklistTransform = <T extends string | number>({
  extra,
  labelMap,
}: {
  labelMap?: Record<T, string>;
  extra?: Record<string, any>;
} = {}) => ({
  input: (values: T[]): AccessibleDropdownOption[] =>
    values?.map((value) => ({
      value,
      label: labelMap?.[value] || `${value}`,
      key: value,
      ...(extra || {}),
    })) || [],
  output: (
    options: AccessibleDropdownOption | AccessibleDropdownOption[] | readonly AccessibleDropdownOption[] | null,
  ): T[] => (Array.isArray(options) ? (options || []).map((option) => option.value as T) : (options as any)),
});
